import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GENRES, INTEREST_TYPES, YES_NO, FUNNEL, IMAGES } from "@/lib/data";
import CalendlyEmbed from "@/components/site/CalendlyEmbed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  interest_type: "", artist_name: "", email: "", instagram: "", spotify: "",
  genre: "", location: "", recent_releases: "", goal_session: "", goal_artist: "",
  actively_releasing: "", long_term: "",
};

const triggerCls = "bg-white/[0.04] border-white/15 text-white h-11 rounded-lg data-[placeholder]:text-white/30";
const contentCls = "bg-[#0A0A0A] border-white/15 text-white";

export const StudioSessions = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.artist_name || !form.email || !form.interest_type) {
      toast.error("Add your artist name, email and what you're interested in.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      toast.success("Application received. I review every one personally.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="sessions" className="relative py-28 lg:py-36 bg-[#0A0A0A] border-y border-white/10" data-testid="sessions-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-16">
        {/* Left: pitch + funnel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="text-[12px] tracking-[0.3em] uppercase text-white/40 mb-5">Studio Sessions</p>
          <h2 className="font-heading uppercase tracking-tight text-white text-4xl lg:text-6xl leading-[0.95]">
            Come make <span className="font-serif-accent italic lowercase normal-case tracking-normal text-gold">music</span>
          </h2>
          <div className="flex items-center gap-2 mt-5 text-white/50 text-[13px] tracking-[0.06em]">
            <MapPin size={15} /> Weekly sessions · London
          </div>
          <p className="text-white/55 mt-6 leading-relaxed text-[15px] max-w-md">
            Come through, make music, build a relationship and develop your sound. Sessions are where partnerships start — this form helps me understand where you're at.
          </p>

          <div className="mt-10">
            <p className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-5">How it grows</p>
            <div className="space-y-0">
              {FUNNEL.map((f, i) => (
                <div key={f} className="flex items-center gap-4 py-3 border-t border-white/10" data-testid={`funnel-${i}`}>
                  <span className="font-serif-accent italic text-gold text-xl w-8">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-white/70 text-[15px]">{f}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 relative rounded-2xl overflow-hidden border border-white/10 aspect-[16/10]" data-testid="sessions-photo">
            <img src={IMAGES.session} alt="Studio session in progress" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 to-transparent" />
          </div>
        </motion.div>

        {/* Right: application form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-7"
        >
          {done ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center h-full flex flex-col justify-center" data-testid="apply-success">
              <div className="font-heading uppercase tracking-tight text-white text-3xl mb-3">Application received</div>
              <p className="text-white/55 leading-relaxed max-w-md mx-auto">
                Thanks for taking the time. I read every application personally and reply to the artists that are the right fit. Keep an eye on your inbox.
              </p>
              <button onClick={() => setDone(false)} className="mt-8 inline-flex items-center gap-2 justify-center text-[13px] tracking-[0.12em] uppercase text-white/70 hover:text-white transition-colors" data-testid="apply-reset">
                Submit another <ArrowRight size={15} />
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-black/40 p-7 lg:p-9 space-y-5" data-testid="apply-form">
              <Field label="I'm interested in *">
                <Select value={form.interest_type} onValueChange={(v) => set("interest_type", v)}>
                  <SelectTrigger data-testid="f-interest" className={triggerCls}><SelectValue placeholder="Choose one" /></SelectTrigger>
                  <SelectContent className={contentCls}>
                    {INTEREST_TYPES.map((t) => <SelectItem key={t} value={t} className="focus:bg-white/10 focus:text-white">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Artist name *"><Input data-testid="f-artist" value={form.artist_name} onChange={(e) => set("artist_name", e.target.value)} placeholder="Your artist name" className="dark-input" /></Field>
                <Field label="Email *"><Input data-testid="f-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="dark-input" /></Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Instagram"><Input data-testid="f-instagram" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@handle" className="dark-input" /></Field>
                <Field label="Spotify"><Input data-testid="f-spotify" value={form.spotify} onChange={(e) => set("spotify", e.target.value)} placeholder="Profile link" className="dark-input" /></Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Genre">
                  <Select value={form.genre} onValueChange={(v) => set("genre", v)}>
                    <SelectTrigger data-testid="f-genre" className={triggerCls}><SelectValue placeholder="Choose one" /></SelectTrigger>
                    <SelectContent className={contentCls}>
                      {GENRES.map((g) => <SelectItem key={g} value={g} className="focus:bg-white/10 focus:text-white">{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Location"><Input data-testid="f-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City" className="dark-input" /></Field>
              </div>

              <Field label="Recent releases"><Input data-testid="f-releases" value={form.recent_releases} onChange={(e) => set("recent_releases", e.target.value)} placeholder="Link or names of recent drops" className="dark-input" /></Field>

              <Field label="What are you looking to achieve from the session?">
                <Textarea data-testid="f-goal-session" value={form.goal_session} onChange={(e) => set("goal_session", e.target.value)} rows={3} placeholder="Tell me what you want out of the session" className="bg-white/[0.04] border-white/15 text-white rounded-lg resize-none placeholder:text-white/30" />
              </Field>

              <Field label="What are your current goals as an artist?">
                <Textarea data-testid="f-goal-artist" value={form.goal_artist} onChange={(e) => set("goal_artist", e.target.value)} rows={3} placeholder="Where are you trying to take this?" className="bg-white/[0.04] border-white/15 text-white rounded-lg resize-none placeholder:text-white/30" />
              </Field>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Are you actively releasing music?">
                  <Select value={form.actively_releasing} onValueChange={(v) => set("actively_releasing", v)}>
                    <SelectTrigger data-testid="f-releasing" className={triggerCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className={contentCls}>
                      {YES_NO.map((y) => <SelectItem key={y} value={y} className="focus:bg-white/10 focus:text-white">{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Interested in a long-term partnership?">
                  <Select value={form.long_term} onValueChange={(v) => set("long_term", v)}>
                    <SelectTrigger data-testid="f-longterm" className={triggerCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className={contentCls}>
                      {YES_NO.map((y) => <SelectItem key={y} value={y} className="focus:bg-white/10 focus:text-white">{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <button type="submit" disabled={loading} data-testid="apply-submit" className="group w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gold text-black text-[13px] tracking-[0.14em] uppercase font-medium hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <>Submit Application <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></>}
              </button>
            </form>
          )}
        </motion.div>
      </div>

      {/* Cal.com — book a discovery call */}
      <div id="book-call" className="max-w-7xl mx-auto px-6 lg:px-10 mt-16 lg:mt-24 scroll-mt-24" data-testid="calendly-block">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-7">
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-2">Quick chat</p>
            <h3 className="font-heading uppercase tracking-tight text-white text-2xl lg:text-4xl leading-none">
              Book a <span className="font-serif-accent italic lowercase normal-case tracking-normal text-gold">call</span>
            </h3>
          </div>
          <p className="text-white/45 text-[14px] max-w-md leading-relaxed">
            A free 30-minute consultation — we'll dig into your goals, where you're at now, and the gap in between. I'll be straight about where I can genuinely help, and where I can't. From there we take the natural next step: a studio session if you're an artist, or mentorship and ongoing sessions if you're a producer.
          </p>
        </div>
        <CalendlyEmbed />
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-[11px] tracking-[0.14em] uppercase text-white/45">{label}</Label>
    {children}
  </div>
);

export default StudioSessions;
